import { getSanityImgUrl } from "@lib/sanity";
import { BlockContent } from "@lib/sanity-components";
import { Heading, PrimaryButton, styled } from "@sourceful/shared-components";
import { SanityGenericCardInstance } from "@sourceful/shared-types";
import { SanityTextAlign } from "@sourceful/shared-types";
import toRem from "@styles/postprocess/conversion/to-rem";
import Link from "next/link";

export const GenericCardWrapper = styled("div", {
  backgroundColor: "$lightest",
  boxShadow: "$lift",
  margin: toRem(8),
  display: "grid",
  gridTemplateRows: "1fr 1fr",
  width: "370px",
  borderRadius: "$medium",
});

const PrimaryLinkWrapper = styled("div", {
  display: "flex",
  justifyContent: "center",
  marginTop: "10px",
  marginBottom: "10px",
});

export const GenericCardBody = styled("div", {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "$inline_m1",

  "@sm": {
    padding: "$block_m3",
  },

  variants: {
    layout: {
      textPanelBottom: { gridRow: "2 / span 1" },
      textPanelTop: {
        gridRow: "1 / span 1",
      },
    },
  },
});

const HeadingWrapper = styled("div", {
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
});

export const GenericCardImagePanel = styled("div", {
  variants: {
    layout: {
      textPanelBottom: {
        borderTopLeftRadius: "$medium",
        borderTopRightRadius: "$medium",
        gridRow: "1 / span 1",
      },
      textPanelTop: {
        borderBottomLeftRadius: "$medium",
        borderBottomRightRadius: "$medium",
        gridRow: "2 / span 1",
      },
    },
  },
});

export const GenericCardStyledImage = styled("img", {
  height: "100%",
  width: "100%",
  objectFit: "cover",

  variants: {
    layout: {
      textPanelBottom: {
        borderTopLeftRadius: "$medium",
        borderTopRightRadius: "$medium",
      },
      textPanelTop: {
        borderBottomLeftRadius: "$medium",
        borderBottomRightRadius: "$medium",
      },
    },
  },
});

const LinksWrapper = styled("div", {
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
});

export interface GenericCardProps {
  card: SanityGenericCardInstance;
  className?: string;
}

const mapTextAlignToJustifyContent = (textAlign: SanityTextAlign) => {
  const map = { center: "center", left: "flex-start", right: "flex-end" };
  return map[textAlign];
};

const GenericCard = ({ card, className }: GenericCardProps) => {
  const {
    textAlign,
    card: { content, primaryCTA, linkCTAs, title, coverImage },
    layout,
  } = card;

  return (
    <GenericCardWrapper className={className}>
      <GenericCardImagePanel layout={layout}>
        {coverImage?.asset && (
          <GenericCardStyledImage
            layout={layout}
            alt={`${title}-cover-image`}
            src={getSanityImgUrl(coverImage?.asset._ref, base => base.width(500))}
          />
        )}
      </GenericCardImagePanel>
      <GenericCardBody layout={layout} css={{ padding: "20px", justifyContent: "space-evenly" }}>
        <HeadingWrapper css={{ textAlign: textAlign || "center" }}>
          <Heading level={3} fontSize={4}>
            {title}
          </Heading>

          {content && <BlockContent value={content} />}
        </HeadingWrapper>

        {primaryCTA && (
          <PrimaryLinkWrapper
            css={{ justifyContent: mapTextAlignToJustifyContent(textAlign || "center") }}
          >
            <Link passHref href={primaryCTA.url} legacyBehavior>
              <PrimaryButton
                css={{ width: "fit-content", marginTop: "20px" }}
                as="a"
                tabIndex={0}
                compact
              >
                {primaryCTA.title || "Read more"}
              </PrimaryButton>
            </Link>
          </PrimaryLinkWrapper>
        )}
        <LinksWrapper css={{ justifyContent: mapTextAlignToJustifyContent(textAlign || "center") }}>
          {linkCTAs && (
            <>
              {linkCTAs.map(button => {
                return (
                  <Link passHref href={button.url} key={button.url} legacyBehavior>
                    <PrimaryButton
                      css={{
                        width: "fit-content",
                        marginTop: "10px",
                        backgroundColor: "#F6F6F6",
                        color: "#000000",
                        fontSize: "$p3",
                        marginLeft: "5px",
                        marginRight: "5px",
                      }}
                      as="a"
                      tabIndex={0}
                      compact
                    >
                      {button.title}
                    </PrimaryButton>
                  </Link>
                );
              })}
            </>
          )}
        </LinksWrapper>
      </GenericCardBody>
    </GenericCardWrapper>
  );
};

export default GenericCard;
